import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from './GuideLayout';
import { notifyError, notifySuccess } from '../../../utils/notify';
import klepsydraPreview from '../../../img/guide-klepsydra-demo.webp';

const TEMPLATE = `„Ja jestem zmartwychwstaniem i życiem” (J 11,25)

Z głębokim żalem zawiadamiamy, że dnia [data śmierci]
zmarł(a) w wieku [wiek] lat

ś.p.
[Imię i Nazwisko]
z domu [nazwisko rodowe]

Msza święta pogrzebowa zostanie odprawiona
w [dzień tygodnia] [data] o godzinie [godzina]
w kościele [nazwa kościoła] w [miejscowość],
po czym nastąpi odprowadzenie na cmentarz [nazwa cmentarza].

Różaniec w intencji Zmarłego(-ej) zostanie odmówiony
[data] o godzinie [godzina] w [miejsce].

O czym zawiadamia pogrążona w smutku Rodzina`;

const MUST_HAVE = [
    { title: 'Symbol i sentencja', text: 'Krzyż (lub inny symbol) i krótki cytat albo werset na górze klepsydry.' },
    { title: 'Imię i nazwisko Zmarłego', text: 'Poprzedzone skrótem „ś.p.”, u kobiet często z nazwiskiem rodowym („z domu…”).' },
    { title: 'Wiek i data śmierci', text: 'Np. „zmarła dnia 12 października 2026 r. w wieku 84 lat”.' },
    { title: 'Termin i miejsce mszy', text: 'Dzień tygodnia, data, godzina i pełna nazwa kościoła z miejscowością.' },
    { title: 'Miejsce pochówku', text: 'Nazwa cmentarza - przy kremacji informacja o pochówku urny.' },
    { title: 'Różaniec (jeśli jest)', text: 'Data, godzina i miejsce modlitwy różańcowej przed pogrzebem.' },
    { title: 'Kto zawiadamia', text: 'Zwykle „pogrążona w smutku Rodzina” albo wymienieni najbliżsi.' },
];

const MISTAKES = [
    { title: 'Dzień tygodnia nie pasuje do daty', text: 'Najczęstszy błąd. Zawsze sprawdź w kalendarzu, czy 15 października to na pewno czwartek.' },
    { title: 'Literówka w imieniu lub nazwisku', text: 'Poproś rodzinę o sprawdzenie pisowni - zwłaszcza nazwiska rodowego i polskich znaków.' },
    { title: 'Niepełna nazwa kościoła', text: 'W większych miastach jest kilka parafii. Podaj wezwanie i miejscowość.' },
    { title: 'Za mała czcionka', text: 'Klepsydrę czyta się z kilku metrów. Imię i nazwisko powinny być największe, a godziny wyraźne.' },
    { title: 'Zbyt długi tekst', text: 'Długa sentencja i lista krewnych zmniejszają najważniejsze informacje. Mniej znaczy czytelniej.' },
    { title: 'Brak wersji do druku', text: 'Plik z edytora tekstu potrafi się „rozjechać” na innym komputerze. Do druku przekazuj PDF.' },
];

const FAQ = [
    {
        question: 'Czym różni się klepsydra od nekrologu?',
        answer: 'W praktyce oba słowa często oznaczają to samo. Klepsydrą zwykle nazywa się ogłoszenie wywieszane (w kościele, na tablicy ogłoszeń, przy domu Zmarłego), a nekrologiem - ogłoszenie w prasie lub internecie.',
    },
    {
        question: 'Jaki format ma klepsydra?',
        answer: 'Najczęściej drukuje się ją w formacie A4, a na większe tablice ogłoszeń w A3. Ważniejszy od formatu jest czytelny układ: czarna ramka, duże imię i nazwisko oraz wyraźne godziny.',
    },
    {
        question: 'Kiedy wywiesza się klepsydry?',
        answer: 'Zazwyczaj zaraz po ustaleniu terminu pogrzebu, tak aby znajomi i sąsiedzi mieli kilka dni na zaplanowanie udziału w ceremonii.',
    },
    {
        question: 'Czy mogę przygotować klepsydrę bez programu graficznego?',
        answer: 'Tak. W demo Nekrodruku edytujesz przykładowy nekrolog bezpośrednio na podglądzie i pobierasz plik - bez logowania i instalowania czegokolwiek. Po założeniu konta wypełniasz formularz, a program sam układa dane w szablonie.',
    },
];

export default function KlepsydraWzor() {
    const copyTemplate = async () => {
        try {
            await navigator.clipboard.writeText(TEMPLATE);
            notifySuccess('Skopiowano wzór klepsydry.');
        } catch {
            notifyError('Nie udało się skopiować - zaznacz tekst i skopiuj go ręcznie.');
        }
    };

    return (
        <GuideLayout
            path='/klepsydra-wzor'
            title='Klepsydra - wzór do edycji i druku | Nekrodruk.pl'
            description='Gotowy wzór klepsydry pogrzebowej do skopiowania i edycji. Sprawdź, co musi zawierać klepsydra, jakie błędy popełnia się najczęściej i jak szybko przygotować plik do druku.'
            keywords='klepsydra wzór, klepsydra wzór do edycji, klepsydra do druku, klepsydra pogrzebowa, wzór klepsydry word, klepsydra pdf'
            heading={<>Klepsydra<em> - wzór do edycji i druku</em></>}
            lead='Zamiast układać klepsydrę w Wordzie, przygotuj ją w generatorze Nekrodruk: zmieniasz tekst na gotowym wzorze i pobierasz plik do druku. Poniżej znajdziesz też wzór tekstu, listę obowiązkowych informacji i najczęstsze błędy.'
            actions={
                <>
                    <Link to='/demo' className='guide_btn guide_btn--light'>
                        Przygotuj klepsydrę w demo
                    </Link>
                    <span className='guide_heroNote'>Bez logowania i bez instalowania programów</span>
                </>
            }
            faq={FAQ}
        >
            <section className='guide_section'>
                <div className='guide_inner'>
                    <div className='guide_showcase'>
                        <figure className='guide_preview'>
                            <img
                                src={klepsydraPreview}
                                alt='Przykładowa klepsydra przygotowana w generatorze Nekrodruk'
                                width='960'
                                height='676'
                            />
                            <figcaption>Klepsydra przygotowana w demo Nekrodruku (wersja demo ma znak wodny)</figcaption>
                        </figure>
                        <div>
                            <span className='guide_kicker'>Najszybciej</span>
                            <h2 className='guide_title'>Gotowa klepsydra w kilka minut</h2>
                            <ol className='guide_steps'>
                                <li>
                                    <strong>Otwórz demo</strong>
                                    <span>Bez zakładania konta - od razu widzisz gotowy wzór klepsydry.</span>
                                </li>
                                <li>
                                    <strong>Zmień tekst na podglądzie</strong>
                                    <span>Kliknij element, wpisz dane Zmarłego, terminy i miejsca. Układ dopasuje się sam.</span>
                                </li>
                                <li>
                                    <strong>Pobierz PDF lub JPG</strong>
                                    <span>Plik gotowy do wydruku w A4 - bez „rozjeżdżania się” jak w edytorze tekstu.</span>
                                </li>
                            </ol>
                            <Link to='/demo' className='guide_btn'>
                                Wypróbuj za darmo
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Wolisz napisać tekst samodzielnie? Oto wzór</h2>
                    <p className='guide_lead'>
                        Zastąp pola w nawiasach kwadratowych właściwymi danymi. Pamiętaj o odmianie -{' '}
                        <strong>zmarł / zmarła</strong>, <strong>Zmarłego / Zmarłej</strong>.
                    </p>
                    <div className='guide_template'>
                        <pre>{TEMPLATE}</pre>
                        <div className='guide_templateActions'>
                            <button type='button' className='guide_btn' onClick={copyTemplate}>
                                Kopiuj wzór
                            </button>
                            <Link to='/demo' className='guide_inlineLink'>
                                Wklej tekst do demo - ramka i układ zrobią się same
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Co musi zawierać klepsydra?</h2>
                    <p className='guide_lead'>
                        Bez tych informacji klepsydra nie spełni swojego zadania - ktoś może nie dotrzeć na mszę albo pomylić
                        cmentarz.
                    </p>
                    <ul className='guide_checklist'>
                        {MUST_HAVE.map((item) => (
                            <li key={item.title}>
                                <strong>{item.title}</strong>
                                <span>{item.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Najczęstsze błędy na klepsydrach</h2>
                    <div className='guide_cards'>
                        {MISTAKES.map((item) => (
                            <article key={item.title} className='guide_card'>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Wzór w Wordzie czy generator?</h2>
                    <div className='guide_text'>
                        <p>
                            Wzór w edytorze tekstu wymaga ręcznego układania: przesuwania linijek, dopasowania czcionek i
                            pilnowania, żeby ramka nie „uciekła” na drugą stronę. Każda zmiana daty czy godziny to ryzyko, że
                            dzień tygodnia przestanie się zgadzać.
                        </p>
                        <p>
                            W <strong>Nekrodruku</strong> wpisujesz dane do formularza, a program sam układa je w szablonie,
                            wylicza dzień tygodnia i przygotowuje gotowy plik <strong>PDF lub JPG</strong> do druku. Te same
                            dane posłużą też do nekrologu, szarfy i tabliczki na krzyż.
                        </p>
                    </div>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <div className='guide_callout'>
                        <div>
                            <h2>
                                Przygotuj klepsydrę<em> w kilka minut</em>
                            </h2>
                            <p>
                                Wypróbuj generator bez logowania - zmień tekst przykładowego nekrologu i pobierz plik.
                                Prowadzisz zakład pogrzebowy? Załóż konto, wypełniaj formularz i korzystaj z własnych
                                szablonów.
                            </p>
                        </div>
                        <div className='guide_actions'>
                            <Link to='/demo' className='guide_btn guide_btn--light'>
                                Przygotuj klepsydrę w demo
                            </Link>
                            <Link to='/register' className='guide_btn guide_btn--ghost'>
                                Załóż konto
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </GuideLayout>
    );
}
